import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  deleteCategory,
  fetchCategories,
} from "../../components/Manual_List/fetch/slices";
import { RootState } from "../../components/Manual_List/fetch/store";
import { Category, Language } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import { useAppDispatch } from "../../components/Manual_List/fetch/hook";
import CircularProgress from "@mui/material/CircularProgress";
import { FaTrash } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import translationManualList from "../../components/Manual_List/translationManualList";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import SearchIcon from "@mui/icons-material/Search";
import { SelectChangeEvent } from "@mui/material/Select";
import translation from "./TranslationList";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { useNavigate } from "react-router-dom";
import { getLabel } from "../../utils/getLabel";

const CategoryList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const categories: Category[] = useSelector(
    (state: RootState) => state.data.categories.data
  );
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const effectiveLanguage = language || "";
  const ml = translationManualList[effectiveLanguage as Language];
  const t = translation[effectiveLanguage as Language];

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await dispatch(fetchCategories(effectiveLanguage));
      setLoading(false);
    }
    fetchData();
  }, [dispatch, language]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const filteredCategories = categories.filter((category) => {
    const label = getLabel(category.name, language) || "";
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const pageCount = Math.ceil(filteredCategories.length / itemsPerPage);

  const handleDeleteCategory = (id: number) => {
    dispatch(deleteCategory(id));
    setIsConfirmationModalOpen(false);
  };

  const openConfirmationModal = (category: Category) => {
    setCategoryToDelete(category);
    setIsConfirmationModalOpen(true);
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col">
          <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-4 w-full">
            <h1 className="text-xl font-semibold">{t.categoryList}</h1>
            <div className="relative w-64">
              <input
                type="text"
                placeholder={ml.search}
                className="px-3 py-2 border rounded-md w-full pr-10 outline-none dark:bg-darkBackground dark:border-purple"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600" />
            </div>
          </div>
          <div className="flex justify-end mb-4 w-full">
            <button
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
              onClick={() => navigate("/add-category")}
            >
              {t.addCategory}
            </button>
          </div>
          <div className="overflow-x-auto rounded-md">
            <table className="min-w-full">
              <thead className="bg-gray-100 dark:bg-darkSecondaryBackground">
                <tr>
                  <th className="py-2 px-4 font-medium text-left">
                    {t.categoryName}
                  </th>
                  <th className="py-2 px-4 font-medium text-left"></th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories
                  .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                  .map((category, index) => (
                    <tr
                      key={category.id}
                      className={`${
                        index !== filteredCategories.length - 1
                          ? "border-b border-gray-300"
                          : ""
                      }`}
                    >
                      <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground">
                        {getLabel(category.name, language)}
                      </td>
                      <td className="py-2 px-4 text-right flex font-extralight space-x-2 items-center bg-white dark:bg-darkSecondaryBackground">
                        <MdEdit
                          className="text-black ml-auto cursor-pointer dark:text-white"
                          size={24}
                          onClick={() => {
                            navigate(`/edit-category/${category.id}`);
                          }}
                        />
                        <FaTrash
                          className="text-red_1 cursor-pointer"
                          size={20}
                          onClick={() => openConfirmationModal(category)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {filteredCategories.length === 0 && (
            <div className="text-center my-4 col-span-full">
              <p className="text-lg text-gray-500">No categories found.</p>
            </div>
          )}
          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
        {loading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
            <CircularProgress />
          </div>
        )}
      </div>
      {categoryToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => handleDeleteCategory(categoryToDelete.id)}
          label={t.deleteConfirmation(categoryToDelete.name)}
        />
      )}
    </div>
  );
};

export default CategoryList;
